import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { SharedGroupsCellProps } from '../types';

/**
 * SharedGroupsCell - Display shared groups as chips
 *
 * Pure presentational component that shows shared groups as chips.
 * Displays up to 2 groups with a "+N more" indicator for additional groups.
 * Shows group descriptions in tooltips.
 *
 * @param doc - The document to display shared groups for
 */
export const SharedGroupsCell: React.FC<SharedGroupsCellProps> = ({ doc }) => {
  const { t } = useTranslation();

  // Try to use populated group details first, fallback to group IDs
  const groupDetails = (doc as any).shared_with_groups_details || [];
  const groups = (doc as any).shared_with_groups || [];

  if ((!groupDetails || groupDetails.length === 0) && (!groups || groups.length === 0)) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('documents.table.noGroups')}
      </Typography>
    );
  }

  // Helper function to get display name for a group
  const getGroupDisplayName = (groupDetail: any, groupId?: string) => {
    if (groupDetail && typeof groupDetail === 'object') {
      if (groupDetail.name) {return groupDetail.name;}
      if (groupDetail.display_name) {return groupDetail.display_name;}
      if (groupDetail.id) {
        return groupDetail.id.length > 8 ? `${groupDetail.id.slice(0, 8)}...` : groupDetail.id;
      }
    }

    // Fallback to group ID
    return groupId && groupId.length > 8 ? `${groupId.slice(0, 8)}...` : groupId || '';
  };

  // Use group details if available, otherwise fall back to group IDs
  const displayGroups = groupDetails.length > 0
    ? groupDetails.map((detail: any, index: number) => ({
        detail,
        groupId: groups[index] || null,
        displayName: getGroupDisplayName(detail, groups[index]),
        tooltip: detail?.description || detail?.name || detail?.display_name || groups[index] || ''
      }))
    : groups.map((groupId: string) => ({
        detail: null,
        groupId,
        displayName: getGroupDisplayName(null, groupId),
        tooltip: groupId
      }));

  const visibleGroups = displayGroups.slice(0, 2);
  const remainingCount = displayGroups.length - visibleGroups.length;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {visibleGroups.map((group: any, index: number) => (
        <Tooltip key={index} title={group.tooltip}>
          <Chip
            label={group.displayName}
            size="small"
            variant="outlined"
            color="secondary"
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <Tooltip title={`${remainingCount} ${t('documents.table.moreGroups')}: ${displayGroups.slice(2).map((g: any) => g.displayName).join(', ')}`}>
          <Chip
            label={`+${remainingCount}`}
            size="small"
            variant="outlined"
            color="secondary"
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

SharedGroupsCell.displayName = 'SharedGroupsCell';
